/** Signing off the mission's drafts (sentio missionagent/integration/editing/signoff.py).
 *
 *  Throughout the mission the summary and the handoff are AI drafts. Once the mission has ended, and the AI has
 *  finished drafting them, anyone who can see the mission may sign them off: the summary with "Edit & Sign Off"
 *  (SummaryEditing), the handoff with "Sign off" here. A sign-off is logged with who and when, and the signed
 *  version is shown from then on as it was signed. */

import { useState } from "react";
import {
  Alert,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Tooltip,
} from "@mui/material";
import { useCookies } from "react-cookie";

import { errorMessage, signOffDraft } from "../api";
import type { SignOff } from "../types";
import { formatAt } from "./MissionHeader";

/** As small as the panels' titles ("MISSION SUMMARY", "RECEIVING FACILITY HANDOFF"): the theme's overline. */
export const titleSizedButtonSx = {
  fontSize: "0.68rem",
  lineHeight: 1.6,
  py: 0.25,
  px: 1,
  minWidth: 0,
  "& .MuiButton-startIcon": { mr: 0.5, "& > *:nth-of-type(1)": { fontSize: "0.85rem" } },
} as const;

/** While the AI drafts it, a draft cannot be signed off: what it signs would change under it. */
export const STILL_DRAFTING = "The AI is still drafting it: sign off once it has finished.";

/** "Kari Nordmann", else the username. */
export const signerName = (signed: SignOff) => signed.name || signed.by;

/** "Signed off · Kari Nordmann · 29 Oct, 14:32", with the username in the tooltip. */
export function SignedOffChip({ signed }: { signed: SignOff }) {
  const who = signed.name ? `${signed.name} (${signed.by})` : signed.by;
  return (
    <Tooltip title={`Version ${signed.version} signed off by ${who}, ${formatAt(signed.at)}`}>
      <Chip
        size="small"
        color="success"
        variant="outlined"
        label={`Signed off · ${signerName(signed)} · ${formatAt(signed.at)}`}
      />
    </Tooltip>
  );
}

type HandoffSignOffProps = {
  missionId: string;
  version: number;
  /** The AI has a newer handoff on its way: signing off waits for it. */
  drafting: boolean;
  /** Called once signed: the page re-reads the mission. */
  onSigned: () => void;
};

/** "Sign off": asks to confirm, then signs off the handoff version shown. It is then final. */
export function HandoffSignOffButton({ missionId, version, drafting, onSigned }: HandoffSignOffProps) {
  const [cookies] = useCookies(["access_token"]);
  const token: string | undefined = cookies.access_token;
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const sign = async () => {
    if (!token) return;
    setBusy(true);
    setError(null);
    try {
      await signOffDraft(token, missionId, "handoff_draft", version);
      setOpen(false);
      onSigned();
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Tooltip title={drafting ? STILL_DRAFTING : "Sign off the handoff as shown"}>
        <span>
          <Button
            variant="contained"
            size="small"
            disabled={drafting || !token}
            onClick={() => {
              setError(null);
              setOpen(true);
            }}
            sx={titleSizedButtonSx}
          >
            Sign off
          </Button>
        </span>
      </Tooltip>
      <Dialog open={open} onClose={() => !busy && setOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Sign off the handoff?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            You sign off version {version} of the handoff, as shown. It is then final: it is kept exactly as signed,
            the AI writes no more handoffs for this mission, and the page and the PDF say who signed it off and
            when. This cannot be undone.
          </DialogContentText>
          {error ? (
            <Alert severity="error" sx={{ mt: 1.5 }}>
              {error}
            </Alert>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)} disabled={busy}>
            Cancel
          </Button>
          <Button variant="contained" disabled={busy} onClick={() => void sign()}>
            Sign off
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
